import type { MountedPlayer } from './mediaPlayerTypes';

export interface MediaSessionArtworkImage {
	src: string;
	sizes?: string;
	type?: string;
}

export interface MediaSessionChapterMetadata {
	title: string;
	artist: string;
	album?: string;
	/** Lock-screen / system now-playing artwork (topic cover). Optional. */
	artwork?: MediaSessionArtworkImage[];
}

export interface MediaSessionPlayerOptions {
	metadata: MediaSessionChapterMetadata;
	onPlay?: () => void;
	onPause?: () => void;
	onPrevious?: () => void;
	onNext?: () => void;
	/** Browser-originated Auto PiP request (Chrome registers this opportunistically). */
	onEnterPictureInPicture?: () => void;
}

export interface MediaSessionControllerEnvironment {
	mediaSession?: MediaSession | null;
	createMetadata?: (metadata: MediaMetadataInit) => MediaMetadata;
}

export interface MediaSessionController {
	mount(player: MountedPlayer, options: MediaSessionPlayerOptions): () => void;
	activate(player: MountedPlayer): void;
}

const MEDIA_SESSION_ACTIONS = [
	'play',
	'pause',
	'seekbackward',
	'seekforward',
	'seekto',
	'previoustrack',
	'nexttrack',
	'stop',
] as const satisfies readonly MediaSessionAction[];

// lib.dom lags Chromium's Auto PiP Media Session action. Keep it isolated so
// supported browsers opt in, while Safari/older Chromium safely reject it.
const ENTER_PICTURE_IN_PICTURE_ACTION = 'enterpictureinpicture';

interface Registration {
	player: MountedPlayer;
	options: MediaSessionPlayerOptions;
	disposed: boolean;
	activateListener: EventListener;
	cleanup: () => void;
}

interface ActiveRegistration {
	registration: Registration;
	generation: number;
	listeners: Array<{ type: string; listener: EventListener }>;
}

type SeekableMediaElement = HTMLMediaElement & { fastSeek?: (time: number) => void };

export function createMediaSessionController(environment?: MediaSessionControllerEnvironment): MediaSessionController {
	const registrations = new WeakMap<MountedPlayer, Registration>();
	let active: ActiveRegistration | null = null;
	let generation = 0;

	const mediaSession = (): MediaSession | null => {
		if (environment) return environment.mediaSession ?? null;
		if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return null;
		return navigator.mediaSession;
	};

	const createMetadata = (metadata: MediaMetadataInit): MediaMetadata | null => {
		try {
			if (environment) return environment.createMetadata?.(metadata) ?? null;
			if (typeof MediaMetadata === 'undefined') return null;
			return new MediaMetadata(metadata);
		} catch {
			return null;
		}
	};

	const isCurrent = (registration: Registration, expectedGeneration: number): boolean => (
		!registration.disposed
		&& active?.registration === registration
		&& active.generation === expectedGeneration
	);

	const setPlaybackState = (session: MediaSession, state: MediaSessionPlaybackState): void => {
		if (!('playbackState' in session)) return;
		try {
			session.playbackState = state;
		} catch {
			// Browsers expose Media Session piecemeal; one unsupported field must not disable the rest.
		}
	};

	const syncMetadata = (registration: Registration, session: MediaSession): void => {
		if (!('metadata' in session)) return;
		const metadata = createMetadata({
			title: registration.options.metadata.title,
			artist: registration.options.metadata.artist,
			album: registration.options.metadata.album ?? 'DeepMox',
			artwork: registration.options.metadata.artwork,
		});
		if (!metadata) return;
		try {
			session.metadata = metadata;
		} catch {
			// Metadata support is optional even when navigator.mediaSession exists.
		}
	};

	const syncPosition = (registration: Registration, expectedGeneration: number, session: MediaSession): void => {
		if (!isCurrent(registration, expectedGeneration) || typeof session.setPositionState !== 'function') return;
		const element = registration.player.element;
		const duration = element.duration;
		if (!Number.isFinite(duration) || duration <= 0) return;
		const currentTime = Number.isFinite(element.currentTime) ? element.currentTime : 0;
		const playbackRate = Number.isFinite(element.playbackRate) && element.playbackRate > 0 ? element.playbackRate : 1;
		try {
			session.setPositionState({
				duration,
				playbackRate,
				position: clamp(currentTime, 0, duration),
			});
		} catch {
			// Position state is independently optional.
		}
	};

	const clearSystemSession = (session: MediaSession): void => {
		setPlaybackState(session, 'none');
		if (typeof session.setPositionState === 'function') {
			try {
				session.setPositionState();
			} catch {
				// Clearing position state is independent from the other cleanup steps.
			}
		}
		if ('metadata' in session) {
			try {
				session.metadata = null;
			} catch {
				// Continue clearing action handlers.
			}
		}
		if (typeof session.setActionHandler === 'function') {
			for (const action of MEDIA_SESSION_ACTIONS) {
				try {
					session.setActionHandler(action, null);
				} catch {
					// Safari may reject individual actions; cleanup the remaining actions.
				}
			}
			try {
				session.setActionHandler(ENTER_PICTURE_IN_PICTURE_ACTION as MediaSessionAction, null);
			} catch {
				// Auto PiP is optional and absent from most current implementations.
			}
		}
	};

	const detachActiveListeners = (current: ActiveRegistration): void => {
		for (const { type, listener } of current.listeners) {
			current.registration.player.element.removeEventListener(type, listener);
		}
		current.listeners.length = 0;
	};

	const clearActive = (registration: Registration, expectedGeneration: number): void => {
		if (!isCurrent(registration, expectedGeneration)) return;
		const current = active;
		active = null;
		generation += 1;
		if (current) detachActiveListeners(current);
		const session = mediaSession();
		if (session) clearSystemSession(session);
	};

	const seekTo = (registration: Registration, expectedGeneration: number, seconds: number, fast: boolean): void => {
		if (!isCurrent(registration, expectedGeneration) || !Number.isFinite(seconds)) return;
		const element = registration.player.element as SeekableMediaElement;
		const max = Number.isFinite(element.duration) && element.duration > 0 ? element.duration : Number.MAX_SAFE_INTEGER;
		const target = clamp(seconds, 0, max);
		try {
			if (fast && typeof element.fastSeek === 'function') element.fastSeek(target);
			else element.currentTime = target;
		} catch {
			return;
		}
		const session = mediaSession();
		if (session) syncPosition(registration, expectedGeneration, session);
	};

	const seekBy = (registration: Registration, expectedGeneration: number, delta: number): void => {
		const current = Number.isFinite(registration.player.element.currentTime) ? registration.player.element.currentTime : 0;
		seekTo(registration, expectedGeneration, current + delta, false);
	};

	const registerActions = (registration: Registration, expectedGeneration: number, session: MediaSession): void => {
		if (typeof session.setActionHandler !== 'function') return;
		const handlers: Record<(typeof MEDIA_SESSION_ACTIONS)[number], MediaSessionActionHandler> = {
			play: () => {
				if (!isCurrent(registration, expectedGeneration)) return;
				try {
					if (registration.options.onPlay) registration.options.onPlay();
					else {
						registration.player.claimOwnerOnNextPlaying = true;
						void registration.player.element.play().catch(() => undefined);
					}
				} catch {
					// A synchronous play failure should not break other system actions.
				}
			},
			pause: () => {
				if (!isCurrent(registration, expectedGeneration)) return;
				if (registration.options.onPause) registration.options.onPause();
				else registration.player.element.pause();
			},
			seekbackward: (details) => {
				const offset = Number.isFinite(details.seekOffset) ? details.seekOffset! : 10;
				seekBy(registration, expectedGeneration, -offset);
			},
			seekforward: (details) => {
				const offset = Number.isFinite(details.seekOffset) ? details.seekOffset! : 10;
				seekBy(registration, expectedGeneration, offset);
			},
			seekto: (details) => {
				if (!Number.isFinite(details.seekTime)) return;
				seekTo(registration, expectedGeneration, details.seekTime!, details.fastSeek === true);
			},
			previoustrack: () => {
				if (!isCurrent(registration, expectedGeneration)) return;
				try {
					registration.options.onPrevious?.();
				} catch {
					// Navigation errors belong to the caller and must not corrupt this session.
				}
			},
			nexttrack: () => {
				if (!isCurrent(registration, expectedGeneration)) return;
				try {
					registration.options.onNext?.();
				} catch {
					// Navigation errors belong to the caller and must not corrupt this session.
				}
			},
			stop: () => {
				if (!isCurrent(registration, expectedGeneration)) return;
				try {
					registration.player.element.pause();
				} finally {
					clearActive(registration, expectedGeneration);
				}
			},
		};
		for (const action of MEDIA_SESSION_ACTIONS) {
			try {
				session.setActionHandler(action, handlers[action]);
			} catch {
				// Register every action independently for partial Safari implementations.
			}
		}
		try {
			session.setActionHandler(ENTER_PICTURE_IN_PICTURE_ACTION as MediaSessionAction, () => {
				if (!isCurrent(registration, expectedGeneration)) return;
				try {
					registration.options.onEnterPictureInPicture?.();
				} catch {
					// Browser-provided PiP requests must never break normal media keys.
				}
			});
		} catch {
			// Register the standard media actions even where Auto PiP is unsupported.
		}
	};

	const attachActiveListeners = (current: ActiveRegistration, session: MediaSession): void => {
		const { registration, generation: expectedGeneration } = current;
		const onPause = () => {
			if (!isCurrent(registration, expectedGeneration)) return;
			setPlaybackState(session, 'paused');
			syncPosition(registration, expectedGeneration, session);
		};
		const onEnded = () => {
			// A chapter handoff keeps the same <audio>; clearing here would erase
			// lock-screen controls between source swap and the next playing event.
			if (registration.player.suppressEndedMediaSessionCleanup) return;
			clearActive(registration, expectedGeneration);
		};
		const onPositionChange = () => syncPosition(registration, expectedGeneration, session);
		for (const [type, listener] of [
			['pause', onPause],
			['ended', onEnded],
			['loadedmetadata', onPositionChange],
			['timeupdate', onPositionChange],
			['seeked', onPositionChange],
			['ratechange', onPositionChange],
		] as Array<[string, EventListener]>) {
			registration.player.element.addEventListener(type, listener);
			current.listeners.push({ type, listener });
		}
	};

	const activate = (registration: Registration): void => {
		if (registration.disposed) return;
		const session = mediaSession();
		if (!session) return;
		if (active?.registration === registration) {
			syncMetadata(registration, session);
			setPlaybackState(session, registration.player.element.paused ? 'paused' : 'playing');
			syncPosition(registration, active.generation, session);
			return;
		}

		const previous = active;
		active = null;
		generation += 1;
		if (previous) {
			detachActiveListeners(previous);
			clearSystemSession(session);
			try {
				previous.registration.player.element.pause();
			} catch {
				// Continue activating the new element even if the old one cannot pause.
			}
		}

		const current: ActiveRegistration = {
			registration,
			generation: ++generation,
			listeners: [],
		};
		active = current;
		attachActiveListeners(current, session);
		registerActions(registration, current.generation, session);
		syncMetadata(registration, session);
		setPlaybackState(session, registration.player.element.paused ? 'paused' : 'playing');
		syncPosition(registration, current.generation, session);
	};

	return {
		mount(player, options) {
			const existing = registrations.get(player);
			if (existing && !existing.disposed) {
				existing.options = options;
				if (active?.registration === existing) activate(existing);
				return existing.cleanup;
			}

			const registration = {} as Registration;
			registration.player = player;
			registration.options = options;
			registration.disposed = false;
			registration.activateListener = () => activate(registration);
			registration.cleanup = () => {
				if (registration.disposed) return;
				if (active?.registration === registration) clearActive(registration, active.generation);
				registration.disposed = true;
				player.element.removeEventListener('play', registration.activateListener);
				registrations.delete(player);
			};
			registrations.set(player, registration);
			player.element.addEventListener('play', registration.activateListener);
			if (!player.element.paused) activate(registration);
			return registration.cleanup;
		},
		activate(player) {
			const registration = registrations.get(player);
			if (registration) activate(registration);
		},
	};
}

function clamp(value: number, min: number, max: number): number {
	return Math.min(max, Math.max(min, value));
}

export const mediaSessionController = createMediaSessionController();
